import React, { useState } from 'react';
import { FACILITY_ROOMS, CORRIDORS } from '../../../game/phaserFacility/facilityLayout';
import { selectBuildingKind } from './selectBuildingKind';
import { workerYardPos, roomDoorPos } from './buildingGeometry';
import { FacilityLighting } from './FacilityLighting';
import { SkyDome } from './SkyDome';
import { GroundPlane } from './GroundPlane';
import { CorridorMesh } from './CorridorMesh';
import { WorkerSprite } from './WorkerSprite';
import { IndustrialBuilding } from './buildings/IndustrialBuilding';
import { GreenhouseBuilding } from './buildings/GreenhouseBuilding';
import { ConstructionSite } from './buildings/ConstructionSite';
import { GhostFootprint } from './buildings/GhostFootprint';
import { SecurityFence } from './buildings/SecurityFence';

/**
 * Root scene component. Receives game state, renders the full 3D facility complex.
 * @param {object} state - full game state
 * @param {string|null} selectedFacilityRoomId - facility room id currently selected
 * @param {function} onRoomClick - called with roomDef when an interactive room is clicked
 */
export function FacilityScene3D({ state, selectedFacilityRoomId, onRoomClick }) {
  const [hoveredRoomId, setHoveredRoomId] = useState(null);

  const unlockedRoomDefs = FACILITY_ROOMS.filter(r => {
    const kind = selectBuildingKind(r, state);
    return kind === 'industrial' || kind === 'greenhouse';
  });

  const doorByStateId = {};
  unlockedRoomDefs.forEach(r => {
    if (r.stateId) doorByStateId[r.stateId] = roomDoorPos(r);
  });

  return (
    <>
      <FacilityLighting unlockedRoomDefs={unlockedRoomDefs} tick={state.tick} />
      <SkyDome tick={state.tick} />
      <GroundPlane />
      <SecurityFence />

      {/* Corridors / yard paths */}
      {CORRIDORS.map(corridor => (
        <CorridorMesh key={corridor.id} corridorDef={corridor} />
      ))}

      {/* Rooms → buildings */}
      {FACILITY_ROOMS.map(roomDef => {
        const kind = selectBuildingKind(roomDef, state);
        const common = {
          roomDef,
          tick: state.tick,
          isSelected: selectedFacilityRoomId === roomDef.id,
          isHovered: hoveredRoomId === roomDef.id,
          onClick: () => onRoomClick(roomDef),
          onPointerEnter: () => setHoveredRoomId(roomDef.id),
          onPointerLeave: () => setHoveredRoomId(null),
        };
        switch (kind) {
          case 'greenhouse':
            return <GreenhouseBuilding key={roomDef.id} {...common} />;
          case 'industrial':
            return <IndustrialBuilding key={roomDef.id} {...common} />;
          case 'construction':
            return <ConstructionSite key={roomDef.id} roomDef={roomDef} onClick={() => onRoomClick(roomDef)} />;
          case 'ghost':
          default:
            return <GhostFootprint key={roomDef.id} roomDef={roomDef} />;
        }
      })}

      {/* Workers: modular, ambiently roaming */}
      {(state.workers || []).map((worker, i) => (
        <WorkerSprite
          key={worker.id}
          worker={worker}
          yardPos={workerYardPos(i)}
          roomDoorPos={doorByStateId[worker.assignedRoomId] || null}
        />
      ))}
    </>
  );
}
